import * as _ from 'lodash';
import toastr from 'toastr';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import apiService from '../../../../service/apiService';
import * as Utility from '../../../../HelperMethods/UtilityFunctions';
import { parseJSONStringToObject, getAllItems } from '../../../../HelperMethods/UtilityFunctions';
import * as TaskUtilities from '../../../../HelperMethods/TaskUtilities';
import { getUsersApi } from '../../../../HelperMethods/apiHelper';
import {
  EditMode,
  ERRORS,
  TASK_ACTION,
  TASK_ATTRIBUTES_NEEDED,
  TASK_SOURCE,
} from '../../../../HelperMethods/constants';
import * as TaskMappers from '../ManageTaskForm/lib/Mapper';
import logUtils from '../../../../HelperMethods/LogUtils';
import {
  setTasks,
  setUsers,
  startPageLoading,
  stopPageLoading,
  types,
  upsertTasks,
  loadFilters,
  upsertFilter as upsertFilterInRedux,
  deleteFilter as deleteFilterInRedux,
  lockTask as lockTaskInRedux,
  updateParentChildTasks,
  upsertWatchers,
  upsertAssetLevelsInfo,
} from './reducer';
import { doesTaskBelongToSelectedArea, updateNormalizedTaskProperties } from '../../../../HelperMethods/TaskUtilities';
// eslint-disable-next-line import/no-cycle
import { getSelectedAreasForTaskViewPage } from '../../../../reducers/appReducer';
import { actionRecordUtils } from '../../../../HelperMethods/LocalStorageUtils';

export function filterTasksData(tasks = []) {
  return async (dispatch) => {
    dispatch({ type: types.FILTERED_DATA, tasks });
  };
}

export function setPageLoading() {
  return (dispatch) => {
    dispatch({ type: types.TASK_PAGE_LOADING });
  };
}

export function setPageLoaded() {
  return (dispatch) => {
    dispatch({ type: types.TASK_PAGE_LOADED });
  };
}

export function setSchedulerPageLoading(loading = false) {
  return (dispatch) => {
    if (loading) {
      dispatch({ type: types.SCHEDULER_PAGE_LOADING });
    } else {
      dispatch({ type: types.SCHEDULER_PAGE_LOADED });
    }
  };
}

export function setiFramePresentState(state) {
  return (dispatch) => {
    try {
      dispatch({ type: types.UPDATE_IFRAME_STATE, iframepresentState: state });
    } catch (e) {
      console.error(e);
    }
  };
}

export function setView(view) {
  return (dispatch) => {
    dispatch({ type: types.CHANGE_TASK_VIEW_PAGE_VIEW, view });
  };
}

async function getParentTasksIfMissing(tasks) {
  const taskIdMap = _.keyBy(tasks, 'id');
  const parentTaskIdsMissing = tasks
    .filter((t) => t.parentTaskId && !taskIdMap[t.parentTaskId])
    .map((t) => t.parentTaskId);
  if (!parentTaskIdsMissing.length) return [];
  const parentTasks = await apiService.getTasksByTaskIds(parentTaskIdsMissing);
  return parentTasks;
}

export function bulkEditTask(tasks) {
  return async (dispatch, getState) => {
    const allTasks = getState().taskViewPageReducer.tasks.items;
    const { workRequests, approvalRequests } = getState().approvalTasksReducer;

    const stask = [];
    const tasksInReducer = [...allTasks, ...workRequests, ...approvalRequests];
    for (const taskObj of tasks) {
      const reqTask = tasksInReducer.find((t) => t.id === taskObj.id);
      stask.push(reqTask);
    }
    dispatch({ type: types.BULK_EDIT_TASKS, tasks: stask });
  };
}

export function loadWatchersDetail(watcherIds) {
  return async (dispatch) => {
    const response = await apiService.getUsersByIDs(_.uniq(watcherIds));
    const watchers = _.get(response, 'data.getUserByIds') || [];
    dispatch(upsertWatchers(_.keyBy(watchers, 'id')));
  };
}

export function loadAssetLevelDetails(assetIds) {
  return async (dispatch) => {
    const assets = await apiService.getAssetsByIds(_.uniq(assetIds));
    dispatch(upsertAssetLevelsInfo(_.keyBy(assets, 'id')));
  };
}

export function loadAllTasks(
  field,
  areas,
  startTimeGreaterThan,
  startTimeLessThan,
  loadSilently,
  selectedTaskTypeGroups = []
) {
  return async (dispatch, getState) => {
    if (!loadSilently) {
      dispatch({ type: types.LOADING_ALL_TASKS });
    }
    try {
      let reqWatcherIds = [];
      let assetLevelsIds = [];
      const { loggedInUser } = getState().appReducer;
      const { watcherDetailsDict = {}, assetLevelsDict = {} } = getState().taskViewPageReducer;
      let tasks = await apiService.bulkDownloadTasks({
        areas: areas.map((a) => a.id),
        fieldId: field.id,
        fromDate: startTimeGreaterThan,
        toDate: startTimeLessThan,
        enableS3Upload: true,
        taskTypeGroupIDs: selectedTaskTypeGroups.map((a) => a.id),
      });
      tasks = tasks.filter((task) => TaskUtilities.shouldShowForLoggedInVendor(task.assigneeIds, loggedInUser));
      tasks = tasks
        .map((t) => {
          const task = _.pick(t, TASK_ATTRIBUTES_NEEDED);
          task.assignee = JSON.stringify(task.assignee || {});
          task.data = JSON.stringify(task.data || {});
          if (task?.watcherIds?.length) {
            const filteredWatcherIds = (task.watcherIds || []).filter((id) => !watcherDetailsDict[id]);
            reqWatcherIds = [...reqWatcherIds, ...(filteredWatcherIds || [])];
          }
          if (!_.isEmpty(task.assetLevelsInfo)) {
            const filteredAssetIds = (Object.values(task.assetLevelsInfo || {}) || []).filter(
              (id) => !assetLevelsDict[id]
            );
            assetLevelsIds = [...assetLevelsIds, ...(filteredAssetIds || [])];
          }
          return task;
        })
        .filter(
          (t) =>
            TaskUtilities.doesTaskBelongToSelectedArea(t, areas) &&
            TaskUtilities.doesTaskBelongToSelectedTaskTypeGroups(t, selectedTaskTypeGroups)
        );
      if (reqWatcherIds?.length) {
        dispatch(loadWatchersDetail(reqWatcherIds));
      }
      if (assetLevelsIds?.length) {
        dispatch(loadAssetLevelDetails(assetLevelsIds));
      }
      const parentTasks = await getParentTasksIfMissing(tasks);
      dispatch({ type: types.LOADED_ALL_TASKS, tasks: [...tasks, ...parentTasks] });
    } catch (e) {
      logUtils.logError(e, 'Loading Tasks Failed');
      dispatch({ type: types.LOADING_ALL_TASKS_FAILED });
    }
  };
}

export function onTasksInsertedOrUpdated(tasks) {
  return (dispatch, getState) => {
    let reqWatcherIds = [];
    let assetLevelsIds = [];
    const { watcherDetailsDict = {}, assetLevelsDict = {} } = getState().taskViewPageReducer;
    const filteredTasks = _.uniqBy(_.reverse(tasks), 'id'); // it may happen subscriptions happened for same task id multiple times, hence here making sure that latest task is being served to reducer
    filteredTasks.forEach((task) => {
      Utility.stripFromTypeName(task);
      Utility.stripFromTypeName(task.assigneeIds);
      Utility.stripFromTypeName(task.schedule);
      const assignee = JSON.parse(task.assignee);
      const data = JSON.parse(task.data);
      // eslint-disable-next-line no-param-reassign
      task.assignee = typeof assignee === 'string' ? assignee : task.assignee;
      task.globalAttributes = parseJSONStringToObject(task.globalAttributes);
      task.approvalInfo = parseJSONStringToObject(task.approvalInfo);
      task.statusAudit = parseJSONStringToObject(task.statusAudit);
      if (task.rulesEvaluated) {
        task.rulesEvaluated = task.rulesEvaluated.map((rule) => parseJSONStringToObject(rule));
      }
      // eslint-disable-next-line no-param-reassign
      task.data = typeof data === 'string' ? data : task.data;
      if (task.template && task.template.length) {
        task.template = task.template.map((item) => {
          return typeof item === 'string' ? JSON.parse(item) : item;
        });
      }
      if (task.watcherIds) {
        const filteredWatcherIds = (task.watcherIds || []).filter((id) => !watcherDetailsDict[id]);
        reqWatcherIds = [...reqWatcherIds, ...filteredWatcherIds];
      }
      if (task.assetLevelsInfo) {
        task.assetLevelsInfo = parseJSONStringToObject(task.assetLevelsInfo);
        const filteredAssetIds = (Object.values(task.assetLevelsInfo || {}) || []).filter((id) => !assetLevelsDict[id]);
        assetLevelsIds = [...assetLevelsIds, ...(filteredAssetIds || [])];
      }
    });
    const parentChildTasks = getState().taskViewPageReducer.parentChildTasks.items;
    const parentChildTasksDict = _.keyBy(parentChildTasks, 'id');
    const tasksInParentChildTaskState = filteredTasks.filter((task) => parentChildTasksDict[task.id]);
    if (tasksInParentChildTaskState?.length) {
      dispatch(updateParentChildTasks(tasksInParentChildTaskState));
    }
    dispatch(upsertTasks(filteredTasks, TASK_SOURCE.SUBSCRIPTION));
    if (reqWatcherIds?.length) {
      dispatch(loadWatchersDetail(reqWatcherIds));
    }
    if (assetLevelsIds?.length) {
      dispatch(loadAssetLevelDetails(assetLevelsIds));
    }
  };
}

export function onTasksDeleted(tasks) {
  return (dispatch) => {
    dispatch({ type: types.DELETE_TASK_SUCCESS, tasks, taskSource: TASK_SOURCE.SUBSCRIPTION });
  };
}

export function bulkDeleteTasks(
  tasks,
  needValidation = false,
  needDependencyMapping = false,
  parentChildGroupTasks = []
) {
  return (dispatch) => {
    dispatch(startPageLoading());

    // dispatch({ type: types.TASK_PAGE_LOADING });
    // after the new Implementation of recurring tasks manifestation...nonVirtualTasks will be []
    return apiService
      .bulkDelete({
        tasksToDelete: tasks,
        needValidation,
        needDependencyMapping,
        parentChildGroupTasks,
      })
      .then((response) => {
        if (needValidation) {
          const data = _.get(response, 'data.rpcService.output', '');
          if (!_.isEmpty(data)) {
            try {
              const output = JSON.parse(data);
              if (output) {
                dispatch(stopPageLoading());
                return _.get(response, 'data.rpcService');
              }
              dispatch(stopPageLoading());

              return {};
            } catch (e) {
              console.error(e);
              dispatch(stopPageLoading());

              return null;
            }
          }
          dispatch(stopPageLoading());
        } else if (response.data.rpcService.success === true) {
          dispatch(stopPageLoading());

          dispatch({ type: types.BULK_DELETE_SUCCESSFUL, tasks });
        } else {
          console.error(`Task Delete Failed: ${response.data.rpcService.message}`);
          throw new Error('Task Deletion Failed');
        }
        return null;
      })
      .catch((e) => {
        logUtils.logError(e, 'Task Deletion Failed!!');
        dispatch({ type: types.BULK_DELETE_FAILED });
      });
  };
}

export function changeGridDate(dateRange) {
  return (dispatch) => {
    const { startDate, endDate } = dateRange;
    const date = { startDate: moment(startDate).format('YYYY-MM-DD'), endDate: moment(endDate).format('YYYY-MM-DD') };
    dispatch({ type: types.GRID_DATE_CHANGED, date });
  };
}

function getDateRange(startDate, endDate) {
  if (endDate < startDate) throw new Error('Start Date is greater than End Date');
  const dates = [startDate];
  for (let i = 0; ; i += 1) {
    const day = moment(dates[i]).add(1, 'day').format('YYYY-MM-DD');
    if (day > endDate) {
      break;
    }
    dates.push(day);
  }
  return dates;
}

export function publishTasks(tasks, username, publishAsPlanned, publishDetails, isDraftState) {
  return function (dispatch) {
    dispatch({ type: types.TASK_PAGE_LOADING });
    const clonedTasks = _.cloneDeep(tasks);
    for (const task of clonedTasks) {
      delete task.asset;
      delete task.assignee;
      delete task.taskType;
      delete task.isDirty;
      delete task.isVirtual;
      task.draftState = !!isDraftState;
    }
    let datesForWhichPublishingIsBeingDone = null;
    if (publishDetails && publishDetails.startDate && publishDetails.endDate) {
      // when publish is being done after running planning or daily engine
      datesForWhichPublishingIsBeingDone = getDateRange(publishDetails.startDate, publishDetails.endDate);
    }
    if (!datesForWhichPublishingIsBeingDone) {
      // when publish is being done after moving tasks in scheduler view
      datesForWhichPublishingIsBeingDone = _.uniq(
        clonedTasks.map((task) => task.startTime).map((time) => moment(time).format('YYYY-MM-DD'))
      );
    }
    return apiService
      .publishTasks(clonedTasks, username, true, publishAsPlanned, datesForWhichPublishingIsBeingDone)
      .then((response) => {
        dispatch({ type: types.TASK_PAGE_LOADED });
        return response;
      })
      .catch((error) => {
        throw error;
      });
  };
}

export function runEngine(
  fieldId,
  areaIds,
  tenantId,
  date,
  userId,
  engineSettings,
  planningStartDate = null, // used by planning engine
  planningEndDate = null, // used by planning engine
  tasks = {},
  currentPlanningStartTime = null, // used by planning engine
  currentPlanningEndTime = null, // used by planning engine
  startTimeDateLte = null, // used by daily engine
  autoFreezTimeDateLte = null // used by daily engine
) {
  return function (dispatch) {
    return new Promise((resolve) => {
      const isPlanningRun = !!(planningStartDate && planningEndDate);
      const timeOutMultiplier = 4;
      const minTimeOut = 300000;
      if (isPlanningRun) {
        // dispatch({ type: types.PLANNING_ENGINE_PROGRESS });
      } else {
        dispatch({ type: types.TASK_PAGE_LOADING });
      }
      const requestId = uuid();

      const mainFunction = () => {
        apiService
          .runEngine(
            requestId,
            fieldId,
            areaIds,
            tenantId,
            date,
            userId,
            engineSettings,
            planningStartDate,
            planningEndDate,
            tasks,
            currentPlanningStartTime,
            currentPlanningEndTime,
            startTimeDateLte,
            autoFreezTimeDateLte
          )
          .then(() => {
            console.debug('response from run engine lambda');
          })
          .catch((error) => {
            console.error(error);
          });
      };
      const timeoutPeriod =
        engineSettings.timeLimit * timeOutMultiplier > minTimeOut
          ? engineSettings.timeLimit * timeOutMultiplier
          : minTimeOut;
      Utility.subscribeFunction(
        apiService.onUpdateRequestId,
        requestId,
        mainFunction,
        timeoutPeriod,
        async (response) => {
          try {
            const res = response.value.data.onUpdateRequestId;
            if (!res.success) {
              throw res;
            }
            const { pathToFile } = JSON.parse(res.message);

            const engineResultResponse = await apiService.getEngineResult(requestId, pathToFile);
            const taskResult = Utility.inflateResponse(engineResultResponse);
            if (Object.keys(taskResult.sequenced).length) {
              dispatch({
                type: types.RUN_ENGINE_SUCCESS,
                tasksDict: taskResult,
                engineParameters: { fieldId, assigneeId: userId, areaIds },
              });
              resolve(taskResult);
            } else if (isPlanningRun) {
              resolve(taskResult);
            } else {
              toastr.error('No tasks returned by Engine.');
              dispatch({ type: types.RUN_ENGINE_FAILED });
            }
          } catch (e) {
            const message =
              // eslint-disable-next-line no-nested-ternary
              e instanceof Error
                ? 'Run Engine Failed'
                : e.message === ERRORS.DECOMPRESSION_INVALID_INPUT_ERROR
                ? 'Empty Routing Engine Response'
                : e.message;
            logUtils.logError(e, message);
            dispatch({ type: types.RUN_ENGINE_FAILED });
          }
        },
        (error) => {
          const message = error instanceof Error ? 'Run Engine Failed' : error.message;
          logUtils.logError(error, message);
          dispatch({ type: types.RUN_ENGINE_FAILED });
        }
      );
    });
  };
}

export function deleteTask(id) {
  return function (dispatch) {
    return apiService
      .deleteTask({ id })
      .then((response) => {
        dispatch({
          type: types.DELETE_TASK_SUCCESS,
          tasks: [response.data.deleteTask],
          taskSource: TASK_SOURCE.NON_SUBSCRIPTION,
        });
      })
      .catch((error) => {
        throw error;
      });
  };
}

export function updateDeletedForRecurrenceDateForTask(task, recurrenceDate) {
  return async (dispatch) => {
    const deletedForRecurrenceDate = _.clone(task.deletedForRecurrenceDates || []);
    deletedForRecurrenceDate.push(recurrenceDate);
    const taskToBeUpdated = {
      id: task.id,
      deletedForRecurrenceDates: deletedForRecurrenceDate,
    };
    const updatedTask = await apiService.upsertTask(taskToBeUpdated);
    dispatch({ type: types.UPDATE_DELETED_FOR_RECURRENCE_DATE_TASK_SUCCESS, task: updatedTask });
  };
}

export function loadUsersForAreas(areas) {
  return async (dispatch) => {
    dispatch({ type: types.LOADING_ALL_USERS });
    let userIds = areas.flatMap((area) => {
      const resourceIdsForArea =
        area.teams
          ?.filter((team) => !team.isDeleted)
          .flatMap((team) =>
            team.resources.filter((resource) => !resource.isDeleted).flatMap((resource) => resource.userId)
          ) || [];
      return resourceIdsForArea;
    });
    userIds = _.unionBy(userIds);
    const promise = [];
    try {
      while (userIds?.length) {
        const seed = userIds.splice(0, 1000);
        promise.push(apiService.getUsersByIDs(seed));
      }
      const response = await Promise.all(promise);
      let users = response.flatMap((res) => {
        return _.get(res, 'data.getUserByIds', []);
      });

      users = users.filter((u) => !u.isDeleted);
      dispatch({ type: types.LOADED_ALL_USERS, users });
    } catch (e) {
      logUtils.logError(e);
      dispatch({ type: types.LOADING_ALL_USERS_FAILED });
    }
  };
}

export function updateTaskTime(task, startTime, endTime) {
  return async (dispatch) => {
    const updateTaskObj = {
      id: task.id,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      duration: endTime.diff(startTime, 'minutes'),
    };
    if (task.isDirty) {
      dispatch({ type: types.UPDATE_TASK_TIME_DIRTY_STATE, updateTaskObj });
    } else {
      dispatch({ type: types.TASK_PAGE_LOADING });
      try {
        const response = await apiService.upsertTask(updateTaskObj);
        dispatch({ type: types.TASK_TIME_UPDATED, task: response });
      } catch (e) {
        logUtils.logError(e, 'Task Updating failed');
      }
    }
  };
}
export function bulkUnAssignTasks(tasks) {
  return async (dispatch) => {
    dispatch({ type: types.TASK_PAGE_LOADING });
    const taskIds = tasks.map((t) => t.id);
    try {
      const response = await apiService.bulkUnAssign(taskIds);
      if (response.data.rpcService.success === true) {
        dispatch({ type: types.BULK_UNASSIGNED_USER_SUCCESSFUL, taskIds });
      } else {
        const errorMsg = `Tasks Bulk Unassigned Failed: ${response.data.rpcService.message}`;
        console.error(errorMsg);
        throw Error(errorMsg); // this error will be caught by catch block
      }
    } catch (e) {
      dispatch({ type: types.BULK_UNASSIGNED_USER_FAILED });
      console.error(e);
      throw e;
    }
  };
}

export function updateTasksFromScheduler(tasks) {
  return (dispatch) => {
    const o = { type: types.UPDATE_TASKS_FROM_SCHEDULER, tasks };
    dispatch(o);
  };
}

export function updateLockedStateOfTask(taskIds, isTaskLocked, isDirty = true) {
  return async (dispatch) => {
    dispatch(lockTaskInRedux(taskIds, isTaskLocked, isDirty));
  };
}

export function changeTaskEditMode(mode) {
  return (dispatch) => {
    dispatch({ type: types.CHANGE_TASK_EDIT_MODE, mode });
  };
}

export function changeTaskPlanningMode(mode) {
  return (dispatch) => {
    dispatch({ type: types.CHANGE_TASK_PLANNING_MODE, mode });
  };
}

export function changeTaskPlanningMessage(message) {
  return (dispatch) => {
    dispatch({ type: types.CHANGE_TASK_PLANNING_MESSAGE, message });
  };
}

export function loadUsersForField(selectedField) {
  const { id: fieldId } = selectedField;
  const params = Utility.getUserSearchGraphQLParamForFieldId(fieldId);
  return async (dispatch) => {
    dispatch({ type: types.LOADING_USERS_AT_FIELD_LEVEL });
    try {
      console.debug(`Loading users for field id ${fieldId}`);
      const users = await getAllItems(getUsersApi, 'searchUsers', params);
      dispatch({ type: types.LOAD_USERS_AT_FIELD_LEVEL_SUCCESS, users });
    } catch (e) {
      logUtils.logError(e, 'Users Loading Failed');
      dispatch({ type: types.LOAD_USERS_AT_FIELD_LEVEL_FAILED });
    }
  };
}

export function createLinkedTask(
  taskToBeCreated,
  { tenantId, tasksEditMode, parentLinkedTask, picklistOptions, stopForSelectedAsset }
) {
  return async (dispatch, getState) => {
    // eslint-disable-next-line no-param-reassign
    taskToBeCreated.linkedTaskIds = [parentLinkedTask.id];
    if (TaskUtilities.isTaskChild(parentLinkedTask)) {
      const { parentTaskId } = parentLinkedTask;
      const allTasks = getState().taskViewPageReducer.tasks.items;
      let parentTask = allTasks.find((t) => t.id === parentTaskId);
      // if task not in store then fetch
      if (!parentTask) {
        const [taskFromDb] = await apiService.getTasksByTaskIds([parentTaskId]);
        parentTask = taskFromDb;
      }
      if (parentTask) {
        const globalAttributesForLinkTask = parentTask.globalAttributes || {};
        taskToBeCreated.globalAttributes =
          typeof globalAttributesForLinkTask === 'string'
            ? JSON.parse(globalAttributesForLinkTask)
            : globalAttributesForLinkTask;

        if (parentTask?.taskType?.rollupGlobalAttributes?.length) {
          const taskGlobalAttributes = (getState().globalAttributeReducer.attributes || []).filter(
            (attr) => attr.entityType === 'task'
          );
          const globalAttributeDict = _.keyBy(taskGlobalAttributes, 'id');
          const rollupGlobalAttributesMerge = {};
          for (const id of parentTask.taskType.rollupGlobalAttributes) {
            if (globalAttributeDict[id]) {
              const key = globalAttributeDict[id].attributeName;
              rollupGlobalAttributesMerge[key] = 0;
            }
          }
          taskToBeCreated.globalAttributes = { ...taskToBeCreated.globalAttributes, ...rollupGlobalAttributesMerge };
        }
      }
      // check if dynamicParentChildTask Parent
      if (parentTask?.taskType?.isDynamicParentChild) {
        taskToBeCreated.parentTaskId = parentTaskId;
        const defaultWorkflowStepValues = TaskUtilities.defaultWorkflowStepInfo();
        taskToBeCreated = { ...taskToBeCreated, ...defaultWorkflowStepValues };
      }
    }
    let taskCreated;
    if (tasksEditMode === EditMode.PUBLISHED) {
      taskCreated = await apiService.upsertTask(taskToBeCreated);
      const taskId = _.get(taskCreated, 'id');
      console.debug(`Task Created with id: ${taskId}`);
      // update parent linked task
      const taskToBeUpdated = {
        id: parentLinkedTask.id,
        linkedTaskIds: [...(parentLinkedTask.linkedTaskIds || []), taskId],
      };
      if (!TaskUtilities.isTaskParent(taskToBeCreated)) {
        // if task just created is not a parent task then only update the parent linked task with just created task ids, otherwise it will be taken care by registerTaskUpdate lambda when it will create child tasks
        try {
          await apiService.upsertTask(taskToBeUpdated);
        } catch (e) {
          console.error('Parent linked task could not be updated');
          throw e;
        }
      }
      dispatch({
        type: types.UPDATE_MERGE_TASK,
        task: taskToBeUpdated,
      });
      toastr.success('Linked Task Created Successfully');
    } else if (tasksEditMode === EditMode.UNPUBLISHED) {
      const taskId = uuid();
      taskCreated = Object.assign(taskToBeCreated, { id: taskId, isDirty: true });
      const taskToBeUpdated = _.cloneDeep(parentLinkedTask);
      Object.assign(taskToBeUpdated, {
        isDirty: true,
        linkedTaskIds: [...(taskToBeUpdated.linkedTaskIds || []), taskId],
      });
      dispatch({ type: types.UPDATE_MERGE_TASK, task: taskToBeUpdated });
      toastr.success('Linked Task Created. Publish to Save Changes');
    }
    const normalizedTask = updateNormalizedTaskProperties(
      taskCreated,
      null,
      picklistOptions,
      tenantId,
      stopForSelectedAsset
    );
    const selectedAreas = getSelectedAreasForTaskViewPage(getState());
    if (doesTaskBelongToSelectedArea(normalizedTask, selectedAreas)) {
      dispatch(upsertTasks([normalizedTask]));
    }
    dispatch(stopPageLoading());
    return taskCreated;
  };
}

export function createTask(
  formValues,
  schema,
  {
    originalTask,
    picklistOptions,
    loggedInUser,
    fieldId,
    tenantId,
    tasksEditMode,
    parentLinkedTask,
    stopForSelectedAsset,
    parentTaskTemplate,
    bulkCreateTasksView,
  }
) {
  return async (dispatch, getState) => {
    dispatch({ type: types.CREATING_TASK });
    try {
      const taskToBeCreated = TaskMappers.mapFormValuesToTask(formValues, schema, {
        originalTask,
        picklistOptions,
        loggedInUser,
        fieldId,
        tasksEditMode,
        taskActionBeingDone: TASK_ACTION.CREATE,
        parentTaskTemplate,
      });
      if (parentLinkedTask) {
        const newLinkedTask = await createLinkedTask(taskToBeCreated, {
          tenantId,
          tasksEditMode,
          parentLinkedTask,
          picklistOptions,
          stopForSelectedAsset,
        })(dispatch, getState);
        return newLinkedTask;
      }
      const taskCreated = await apiService.upsertTask(taskToBeCreated);

      if (taskCreated) {
        if (bulkCreateTasksView) {
          toastr.success('Task(s) with criteria created Successfully!!');
        } else {
          console.debug(`Task Created with id: ${taskCreated.id}`);
          toastr.success('Task Created Successfully!!');
          const normalizedTask = updateNormalizedTaskProperties(
            taskCreated,
            null,
            picklistOptions,
            tenantId,
            stopForSelectedAsset
          );
          const selectedAreas = getSelectedAreasForTaskViewPage(getState());
          if (doesTaskBelongToSelectedArea(normalizedTask, selectedAreas)) {
            dispatch(upsertTasks([normalizedTask]));
          }
        }

        dispatch(stopPageLoading());
      }

      return taskCreated;
    } catch (e) {
      dispatch({ type: types.CREATED_TASK_FAILED });
      throw e;
    }
  };
}

export function updateTask(
  formValues,
  schema,
  {
    originalTask,
    picklistOptions,
    loggedInUser,
    fieldId,
    tenantId,
    tasksEditMode,
    stopForSelectedAsset,
    parentTaskTemplate,
    childTasksDictByWorkflowId = {},
  }
) {
  // eslint-disable-next-line consistent-return
  return async (dispatch) => {
    dispatch({ type: types.UPDATING_TASK });

    let taskToBeUpdated = TaskMappers.mapFormValuesToTask(formValues, schema, {
      originalTask,
      picklistOptions,
      loggedInUser,
      fieldId,
      tasksEditMode,
      taskActionBeingDone: TASK_ACTION.UPDATE,
      parentTaskTemplate,
      childTasksDictByWorkflowId,
    });
    if (tasksEditMode === EditMode.PUBLISHED) {
      try {
        const actionRecord = actionRecordUtils.GET_ACTION_RECORD();
        if (actionRecord) {
          taskToBeUpdated = { ...taskToBeUpdated, modifiedBy: actionRecord.assigneeUserName, ignoreModifiedBy: true };
        }
        const taskResponse = await apiService.upsertTask(taskToBeUpdated);
        const taskId = _.get(taskResponse, 'id');
        if (taskId === taskToBeUpdated.id) {
          toastr.success('Task Updated Successfully!');
          dispatch({
            type: types.UPDATED_TASK_SUCCESSFULLY,
            task: taskResponse,
            picklistOptions,
            tenantId,
            stopForSelectedAsset,
          });
          return { ...taskResponse, donotRedirect: !!actionRecord };
        }
        // eslint-disable-next-line no-throw-literal
        throw { message: 'Task could not be updated' };
      } catch (e) {
        dispatch({ type: types.UPDATED_TASK_FAILED });
        throw e;
      }
    } else if (tasksEditMode === EditMode.UNPUBLISHED) {
      taskToBeUpdated.isDirty = true;
      dispatch({ type: types.UPDATED_TASK_SUCCESSFULLY, task: taskToBeUpdated, picklistOptions, stopForSelectedAsset });
      toastr.success('Task Updated. Publish to Save Changes');
      return taskToBeUpdated;
    }
  };
}

export function approveTask(
  formValues,
  schema,
  { originalTask, picklistOptions, loggedInUser, fieldId, tenantId, tasksEditMode, stopForSelectedAsset }
) {
  return async (dispatch) => {
    dispatch({ type: types.APPROVING_TASK });
    const taskToBeApproved = TaskMappers.mapFormValuesToTask(formValues, schema, {
      originalTask,
      picklistOptions,
      loggedInUser,
      fieldId,
      tasksEditMode,
      taskActionBeingDone: TASK_ACTION.APPROVE,
    });
    if (tasksEditMode === EditMode.PUBLISHED) {
      try {
        const taskResponse = await apiService.upsertTask(taskToBeApproved);
        const taskId = _.get(taskResponse, 'id');
        if (taskId === taskToBeApproved.id) {
          toastr.success('Task Confirmed Successfully!');
          dispatch({
            type: types.APPROVED_TASK_SUCCESSFULLY,
            task: taskResponse,
            picklistOptions,
            tenantId,
            stopForSelectedAsset,
          });
        } else {
          // eslint-disable-next-line no-throw-literal
          throw { message: 'Task could not be Confirmed' };
        }
      } catch (e) {
        dispatch({ type: types.APPROVED_TASK_FAILED });
        throw e;
      }
    } else if (tasksEditMode === EditMode.UNPUBLISHED) {
      taskToBeApproved.isDirty = true;
      dispatch({
        type: types.APPROVED_TASK_SUCCESSFULLY,
        task: taskToBeApproved,
        picklistOptions,
        stopForSelectedAsset,
      });
      toastr.success('Task Confirmed. Publish to Save Changes');
    }
  };
}

export function setEmptyTasksAndUsers() {
  return (dispatch) => {
    dispatch(setTasks([]));
    dispatch(setUsers([]));
  };
}
export function startPageLoadingTask() {
  return async (dispatch) => {
    dispatch(startPageLoading());
  };
}

export function stopPageLoadingTask() {
  return async (dispatch) => {
    dispatch(stopPageLoading());
  };
}

export function editBulkTasks(tasks, username, tasksEditMode) {
  return async (dispatch) => {
    if (tasksEditMode === EditMode.PUBLISHED) {
      dispatch(startPageLoading());
      try {
        const clonedTasks = _.cloneDeep(tasks);
        for (const task of clonedTasks) {
          delete task.asset;
          delete task.assignee;
          delete task.taskType;
          delete task.isDirty;
          delete task.isVirtual;
          delete task.parentTask;
        }
        const response = await apiService.publishTasks(clonedTasks, username, false, false);
        if (_.get(response, 'data.rpcService.success')) {
          dispatch(upsertTasks(tasks));
          dispatch(stopPageLoading());
          toastr.success(`${tasks.length} Task(s) Updated`);
        } else {
          throw response;
        }
      } catch (e) {
        logUtils.logError(e, 'Update Tasks failed');
        dispatch(stopPageLoading());
        throw e;
      }
    } else if (tasksEditMode === EditMode.UNPUBLISHED) {
      const updatedTasks = tasks.map((task) => {
        task.isDirty = true;
        return task;
      });
      dispatch(upsertTasks(updatedTasks));
    }
  };
}

export function loadUsersSubscription({ usersInfo, selectedAreas, fieldId }) {
  return async (dispatch, getState) => {
    try {
      const { loggedInUser } = getState().appReducer;
      // updated user belong to the selected Area needed in future
      const vendorId = _.get(loggedInUser, 'vendorId');

      if (vendorId) {
        // filter out the user with same vendorId
        const filteredUserwithVendorIdAsLoggedInUser = usersInfo.filter(
          (user) => user.vendorId && user.vendorId === vendorId
        );
        dispatch({
          type: types.LOAD_TASK_USERS_SUBSCRIPTION_SUCCESS,
          usersInfo: filteredUserwithVendorIdAsLoggedInUser,
          selectedAreas,
          fieldId,
        });
      }
      if (!vendorId) {
        dispatch({ type: types.LOAD_TASK_USERS_SUBSCRIPTION_SUCCESS, usersInfo, selectedAreas, fieldId });
      }
    } catch (e) {
      console.error(e);
      dispatch({ type: types.LOAD_TASK_USERS_SUBSCRIPTION_FAILED });
    }
  };
}
export function pushTaskToState(task) {
  return (dispatch) => {
    dispatch({ type: types.UPDATE_MERGE_TASK, task });
  };
}

export function toggleWatcher(task, loggedInUserId) {
  return async (dispatch) => {
    const oldWatcherIds = task.watcherIds;
    try {
      const loggedInUserIndex = task.watcherIds?.indexOf(loggedInUserId);
      let newWatcherIds = _.clone(task.watcherIds);
      if (loggedInUserIndex > -1) {
        newWatcherIds.splice(loggedInUserIndex, 1);
      } else {
        newWatcherIds = newWatcherIds ?? [];
        newWatcherIds.push(loggedInUserId);
      }
      const taskToBeUpdated = { id: task.id, watcherIds: newWatcherIds };
      dispatch({ type: types.UPDATE_MERGE_TASK, task: taskToBeUpdated });
      const response = await apiService.upsertTask(taskToBeUpdated);
      const taskId = _.get(response, 'id');
      if (!taskId) {
        throw new Error(`task ${task.id} not updated for watcher`);
      }
    } catch (e) {
      toastr.error('Task Watcher not updated');
      logUtils.logError(e);
      const revertUpdatedTask = { id: task.id, watcherIds: oldWatcherIds };
      dispatch({ type: types.UPDATE_MERGE_TASK, task: revertUpdatedTask });
    }
  };
}

function updateToTaskStateInReducer(getState, taskToBeMerged, dispatch) {
  const tasksItems = getState().taskViewPageReducer.tasks.items;
  const reqtask = (tasksItems || []).find((t) => t.id === taskToBeMerged.id);
  if (reqtask) {
    dispatch({ type: types.UPDATE_MERGE_TASK, task: taskToBeMerged });
  }
  const isInParentChildTaskState = getState().taskViewPageReducer.parentChildTasks.items.find(
    (t) => t.id === taskToBeMerged.id
  );
  if (isInParentChildTaskState) {
    dispatch({ type: types.UPDATE_PARENT_CHILD_MERGE_TASK, task: taskToBeMerged });
  }
}

export function addAssigneeToTask(task, assignee) {
  return async (dispatch, getState) => {
    const oldAssignee = task.assignee;
    const oldAssigneeIds = task.assigneeIds;
    const { usersAtFieldLevel } = getState().taskViewPageReducer;
    try {
      const asigneeToPush = {
        assigneeId: assignee.id,
        isDeleted: false,
        sequence: null,
        vendorId: assignee.vendorId ?? null,
      };
      const assigneeObject = _.clone(parseJSONStringToObject(oldAssignee));
      assigneeObject[assignee.id] = assignee;
      let newAssigneeIds = _.clone(oldAssigneeIds);
      if (task?.taskType?.needEquipment) {
        newAssigneeIds = Utility.mergeAssigneesForNeedsEquipmentTaskType(
          [asigneeToPush],
          oldAssigneeIds,
          usersAtFieldLevel
        );
        const addedAssignee = newAssigneeIds.find((val) => val.assigneeId === assignee.id);
        if (addedAssignee?.operatorId) {
          const operator = usersAtFieldLevel.find((user) => user.id === addedAssignee.operatorId);
          assigneeObject[operator.id] = operator;
        }
      } else {
        newAssigneeIds.splice(0, 0, asigneeToPush);
        newAssigneeIds = _.uniqBy(newAssigneeIds, 'assigneeId');
      }
      const newAssignee = JSON.stringify(assigneeObject);
      const taskToBeMerged = { id: task.id, assigneeIds: newAssigneeIds, assignee: newAssignee };
      updateToTaskStateInReducer(getState, taskToBeMerged, dispatch);
      const taskToBeUpdated = { id: task.id, assigneeIds: newAssigneeIds };
      const response = await apiService.upsertTask(taskToBeUpdated);
      const taskId = _.get(response, 'id');
      if (!taskId) {
        throw new Error(`assignee ${assignee.id} didn't get added to task ${task.id}`);
      }
    } catch (e) {
      toastr.error('Task Assignee not updated');
      logUtils.logError(e);
      const revertUpdatedTask = { id: task.id, assigneeIds: oldAssigneeIds, assignee: oldAssignee };
      updateToTaskStateInReducer(getState, revertUpdatedTask, dispatch);
    }
  };
}

export function removeAssigneeFromTask(task, assignee) {
  return async (dispatch, getState) => {
    const oldAssignee = task.assignee;
    const oldAssigneeIds = task.assigneeIds;
    try {
      const assigneeObject = _.clone(parseJSONStringToObject(oldAssignee));
      delete assigneeObject[assignee.id];
      const newAssigneeIds = _.clone(oldAssigneeIds);
      const assigneeIndex = newAssigneeIds.findIndex((a) => a.assigneeId === assignee.id);
      if (assigneeIndex > -1) {
        newAssigneeIds[assigneeIndex] = _.clone(newAssigneeIds[assigneeIndex]);
        newAssigneeIds[assigneeIndex].isDeleted = true;
        const { operatorId } = newAssigneeIds[assigneeIndex];
        if (operatorId) {
          delete assigneeObject[operatorId];
          const operatorIndex = newAssigneeIds.findIndex((a) => a.assigneeId === operatorId);
          if (operatorIndex > -1) {
            newAssigneeIds[operatorIndex] = _.clone(newAssigneeIds[operatorIndex]);
            newAssigneeIds[operatorIndex].isDeleted = true;
          }
        }
      }
      const newAssignee = JSON.stringify(assigneeObject);
      const taskToBeMerged = { id: task.id, assigneeIds: newAssigneeIds, assignee: newAssignee };
      updateToTaskStateInReducer(getState, taskToBeMerged, dispatch);
      const taskToBeUpdated = { id: task.id, assigneeIds: newAssigneeIds };
      const response = await apiService.upsertTask(taskToBeUpdated);
      const taskId = _.get(response, 'id');
      if (!taskId) {
        throw new Error(`assignee ${assignee.id} didn't get removed from task ${task.id}`);
      }
    } catch (e) {
      toastr.error('Task Assignee not updated');
      logUtils.logError(e);
      const revertUpdatedTask = { id: task.id, assigneeIds: oldAssigneeIds, assignee: oldAssignee };
      updateToTaskStateInReducer(getState, revertUpdatedTask, dispatch);
    }
  };
}

export function removeAllAssigneeFromTask(task) {
  return async (dispatch, getState) => {
    const oldAssignee = task.assignee;
    const oldAssigneeIds = task.assigneeIds;
    try {
      const newAssignee = '{}';
      const oldAssigneeList = _.cloneDeep(oldAssigneeIds);
      const newAssigneeIds = oldAssigneeList.map((e) => {
        // eslint-disable-next-line no-underscore-dangle
        const oldAssignee_ = _.clone(e);
        oldAssignee_.isDeleted = true;
        return oldAssignee_;
      });
      const taskToBeMerged = { id: task.id, assigneeIds: newAssigneeIds, assignee: newAssignee };
      updateToTaskStateInReducer(getState, taskToBeMerged, dispatch);
      const taskToBeUpdated = { id: task.id, assigneeIds: newAssigneeIds };
      const response = await apiService.upsertTask(taskToBeUpdated);
      const taskId = _.get(response, 'id');
      if (!taskId) {
        throw new Error(`assignees didn't get removed from task ${task.id}`);
      }
    } catch (e) {
      toastr.error('Task Assignee not updated');
      logUtils.logError(e);
      const revertUpdatedTask = { id: task.id, assigneeIds: oldAssigneeIds, assignee: oldAssignee };
      updateToTaskStateInReducer(getState, revertUpdatedTask, dispatch);
    }
  };
}

export function updateApprovalMatrixInfoOfATask(data) {
  return async () => {
    try {
      const response = await apiService.approveTask(data);
      if (!response) {
        throw new Error('Failed to perform action');
      }
      toastr.success('Succesfully performed action');
      return response;
    } catch (e) {
      toastr.error('Failed to perform action');
      logUtils.logError(e);
      throw e;
    }
  };
}

export function loadSavedFiltersOfUser(userId) {
  return async (dispatch) => {
    try {
      const filters = await apiService.getAllSavedFiltersOfAUser(userId);
      dispatch(loadFilters(filters));
    } catch (e) {
      dispatch(loadFilters([]));
      logUtils.logError(e, 'Loading Filters Failed');
    }
  };
}

export function upsertFilter(filterData) {
  return async (dispatch, getState) => {
    try {
      const {
        appReducer: { loggedInUser },
      } = getState();
      const reqObj = { userId: loggedInUser.id, ...filterData };
      const updatedFilter = await apiService.upsertFilterApi(reqObj);
      if (!updatedFilter) {
        throw new Error('Failed to perform action');
      }
      dispatch(upsertFilterInRedux(updatedFilter));
      toastr.success('Succesfully performed action');
      return updatedFilter;
    } catch (e) {
      toastr.error('Failed to perform action');
      logUtils.logError(e);
      throw e;
    }
  };
}

export function deleteFilter(id) {
  return async (dispatch, getState) => {
    try {
      const {
        appReducer: { loggedInUser },
      } = getState();
      const res = await apiService.deleteFilterApi({ id, userId: loggedInUser.id, isDeleted: true });
      if (!res) {
        throw new Error('Failed to perform action');
      }
      dispatch(deleteFilterInRedux(id));
      toastr.success('Filter deleted succesfully');
      return { id };
    } catch (e) {
      toastr.error('Failed to perform action');
      logUtils.logError(e);
      throw e;
    }
  };
}

export function onConfirmTask(task) {
  return async (dispatch) => {
    try {
      await apiService.confirmTasks(task.id);
      const tasks = await apiService.getTasksByTaskIds([task.id]);
      const updatedTask = Array.isArray(tasks) && tasks.length ? tasks[0] : undefined;
      if (!updatedTask) {
        throw new Error('Could not fetch updated task');
      }
      dispatch({ type: types.UPDATE_MERGE_TASK, task: updatedTask });
      toastr.success('Task Confirmed Successfully!');
      return null;
    } catch (e) {
      toastr.error('Confirming Task failed');
      logUtils.logError(e);
      return null;
    }
  };
}

export function updateSelectedUserIds(userIds) {
  return (dispatch) => {
    dispatch({ type: types.UPDATE_SELECTED_USER_IDS, userIds });
  };
}
